/**
 * File to initialize the instantsearch.
 * This includes the algolia search client to use, which includes hit injection.
 */

import { liteClient as algoliasearch } from 'algoliasearch/lite';
import type { LiteClient } from 'algoliasearch/lite';
import { stardust } from '@eidellev/adonis-stardust/client';
import browse from './constructors/browse';
import { getCustomerId } from '../utils';

const client = algoliasearch(ALGOLIA_INFO.APP_ID, ALGOLIA_INFO.SEARCH_KEY);

/** Indicates if the user is logged in */
const isLoggedIn = !!getCustomerId();

/** Cache for API responses, so it doesn't need to query Millbank API if it's the same call */
const apiCache = new Map<string, any>();

/** Debounce timer for showing loading indicator (avoid flicker) */
let loadingTimer: ReturnType<typeof setTimeout> | null = null;
const LOADING_CLASS = 'algolia-loading';

const showLoading = () => {
  // console.debug('Showing loading class for search augmentation');
  document.body.classList.add(LOADING_CLASS);
};
const hideLoading = () => {
  // console.debug('Hiding loading class for search augmentation');

  if (loadingTimer) {
    clearTimeout(loadingTimer);
  }
  loadingTimer = null;

  document.body.classList.remove(LOADING_CLASS);
};

// Support async search injection...
// https://support.algolia.com/hc/en-us/articles/12683491573649-Can-I-perform-an-async-function-within-transformItems-on-the-hits-instantSearch-widget
const search = client.search;
// @ts-ignore: Docs say to do this... I think it's just complaining about not having explicit types...
client.search = async (queries) => {
  // Start the loading timer, for debounce (which I'm not using...)
  loadingTimer = setTimeout(showLoading, 0);

  // Perform the default algolia search
  const algoliaResponse = await search(queries);

  const { results } = algoliaResponse;
  // console.debug('algolia search response', algoliaResponse);
  const firstResult = results[0];
  // TODO: Fix types
  const { hits, facetHits } = (firstResult as any) || {};

  // Early return if it's got facet hits because we're just handling the hits, not facet searching...
  if (facetHits) {
    // console.debug('algolia search facetHits', facetHits);
    hideLoading();
    return algoliaResponse;
  }

  // console.debug('algolia search hits', hits);

  // If not logged in or no hits, just return the response as-is
  if (!isLoggedIn || !hits?.length) {
    hideLoading();
    return algoliaResponse;
  }

  // Grab additional data we need to inject into the algolia data
  const hitsMap = new Map<string, any>(
    hits.map((item) => [item.objectID, item])
  );

  const ids = Array.from(hitsMap.keys());
  const pricingCacheKey = `pricing:${ids.sort().join(',')}`;
  let millbankPricingResponse: any = [];

  if (apiCache.has(pricingCacheKey)) {
    millbankPricingResponse = apiCache.get(pricingCacheKey);
    // console.debug('==> Using cached millbankResponse', millbankPricingResponse);
  } else {
    const millbankPricingUrl = stardust.route(
      'api.products.pricing',
      {},
      { qs: { id: ids } }
    );
    try {
      const pricingResponse = await fetch(millbankPricingUrl);
      millbankPricingResponse = await pricingResponse.json();
      // console.debug('==> Fetched fresh millbankResponse', millbankPricingResponse);
      apiCache.set(pricingCacheKey, millbankPricingResponse);
    } catch (error) {
      console.error('Error injecting additional search data', error);
      return algoliaResponse;
    }
  }

  // Merge the data back together before returning it
  for (const priceData of millbankPricingResponse) {
    // On the off chance that Algolia has a product that doesn't exist anymore, skip augmenting it
    if (!priceData) {
      continue;
    }

    // The objectID in Algolia is a string, so we need to convert to string for lookup
    const objectID = priceData.productId.toString();

    hitsMap.set(objectID, {
      ...hitsMap.get(objectID),
      // Add any data we need here:
      price: priceData.price,
    });
  }

  const finalResults = Array.from(hitsMap.values());
  console.debug('search finalResults', finalResults);

  // Replace the hits with our updated ones
  // @ts-ignore: Type issues...
  firstResult.hits = finalResults;

  hideLoading();

  return algoliaResponse;
};

/**
 * Initialize instantsearch.
 * Can accept a search client, but defaults to the one defined in this file.
 * @param searchClient Algolia search client
 */
export default function(searchClient: LiteClient = client) {
  console.debug('Initializing browse');
  browse(searchClient);
}
